import { isNil, propOr } from 'ramda'

/**
 * Writes a cookie with passed name, value and expiry days
 *
 * @param name {String}
 * @param value {String}
 * @param exdays {Number}
 * @param sameSite {String}
 * @param domain {String}
 */
export const setCookie = (name, value, exdays, sameSite = 'lax', domain = '') => {
    const d = new Date()
    d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000))
    const expires = `expires=${d.toUTCString()}`
    document.cookie = `${name}=${value};${expires};SameSite=${sameSite}${domain ? `;domain=${domain}` : ''};path=/`
}

/**
 * Returns true if cookie with passed name exists
 *
 * @param name
 * @returns {boolean}
 */
export const hasCookie = name => (document.cookie.indexOf(`${name}`) > -1)

/**
 * Get all saved cookies
 *
 * @returns {array}
 */
export const getCookies = () => {
    const cookies = []
    const cookieStrings = document.cookie.split(';')
    const regex = new RegExp('^\\s*([^=]+)\\s*=\\s*(.*?)$')

    cookieStrings.forEach((cookieStr) => {
        const match = regex.exec(cookieStr)
        if (match !== null) {
            cookies.push({
                name: match[1],
                value: match[2],
            })
        }
    })

    return cookies
}

/**
 * Get cookie by name
 *
 * @param name {string}
 * @returns {object}
 */
export const getCookie = name => propOr(null, 'value', getCookies().find(x => x.name === name))

const escapeRegexString = regex => regex.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&')

/**
 * Set cookie expiry to past date in order to remove it
 *
 * @param pattern
 * @param path
 * @param domain
 */
export const removeCookie = (pattern, path = '', domain = '') => {
    if (!(pattern instanceof RegExp)) {
        // pattern is no regex, so let's put it into regex for easier manipulation
        pattern = new RegExp(`^${escapeRegexString(pattern)}$`) // eslint-disable-line no-param-reassign
    }

    const savedCookies = getCookies()
    savedCookies.forEach((savedCookie) => {
        const match = pattern.exec(savedCookie.name)
        if (match === null) {
            return
        }

        let domainString = ''
        if (domain) {
            domainString = `;domain=${domain}`
        }

        document.cookie = `${savedCookie.name}= ;path=${path || '/'}${domainString};expires=Thu, 01 Jan 1970 00:00:00 GMT`
    })
}

/**
 * Execute optin function for accepted cookies, if given
 *
 * @param config
 * @param acceptedCookieIds
 */
export const executeCookieOptins = (config, acceptedCookieIds) => {
    acceptedCookieIds.forEach((cookieId) => {
        const cookie = config.cookies.find(x => x.id === cookieId)

        if (isNil(cookie) || isNil(cookie.onAccepted)) return
        cookie.onAccepted()
    })
}

/**
 * Returns false if there are no requirements or if required cookies were not accepted
 *
 * @param array requiredCookies
 * @param array acceptedCookies
 * @returns boolean
 */
export const hasCookieUnacceptedRequirement = (requiredCookies, acceptedCookies) => (requiredCookies || []).some(x => !acceptedCookies.includes(x))
